/**
 * Refine-agent clarification extraction for the AskAsper message processor.
 * Split out of messageProcessor.ts; consumed by both the SSE and history paths there.
 */

import type { AgenticChatMessage } from '../types';
import {
    normalizeClarificationMatches,
    normalizeClarificationTerms,
    parseClarificationContent,
} from '../utils/parseClarificationContent';

/** Clarification fields written onto additional_kwargs (empty when the turn isn't a clarification). */
export type ClarificationKwargs = Pick<
    NonNullable<AgenticChatMessage['additional_kwargs']>,
    'needs_clarification' | 'selected_entities' | 'clarification_resolved'
>;

/**
 * Resolve the refine-agent clarification fields for an interrupt turn.
 *
 * Preferred path: the backend sends `needs_clarification` / `selected_entities` in
 * `additional_kwargs` and `content` is already human-readable prose, so content is left as-is.
 * `selected_entities` are entities in the same question the backend matched unambiguously —
 * they are NOT the user's answer, so they never imply `clarification_resolved`.
 *
 * Legacy fallback: older backends embedded the whole payload as a dict string in `content`;
 * parse it out and swap content for the human message it carries.
 */
export function extractClarification(
    kwargs: { needs_clarification?: unknown; selected_entities?: unknown; clarification_resolved?: unknown; interrupt?: unknown },
    content: string | undefined,
): { content: string | undefined; contentRewritten: boolean; fields: ClarificationKwargs } {
    const needsRaw = kwargs.needs_clarification;
    if (Array.isArray(needsRaw) && needsRaw.length > 0) {
        const needs_clarification = normalizeClarificationTerms(needsRaw);
        if (needs_clarification.length > 0) {
            return {
                content,
                contentRewritten: false,
                fields: {
                    needs_clarification,
                    selected_entities: normalizeClarificationMatches(kwargs.selected_entities),
                    clarification_resolved: kwargs.clarification_resolved === true,
                },
            };
        }
    }

    if (kwargs.interrupt !== true) return { content, contentRewritten: false, fields: {} };

    const clarification = parseClarificationContent(content);
    if (clarification == null) return { content, contentRewritten: false, fields: {} };

    return {
        content: clarification.message,
        contentRewritten: true,
        fields: {
            needs_clarification: clarification.needs_clarification,
            selected_entities: clarification.selected_entities,
            clarification_resolved: false,
        },
    };
}
